import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Globe, MapPin, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Container, ShoreBadge, PlanBadge, GoldButton } from "@/components/shared";

export default function ListingDetail() {
  const { slug } = useParams();
  const [listing, setListing] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ from_name: "", from_email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    setNotFound(false);
    setSent(false);
    api
      .get(`/listings/${slug}`)
      .then((r) => setListing(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post(`/listings/${listing.id}/enquiries`, form);
      setSent(true);
      setForm({ from_name: "", from_email: "", message: "" });
      toast.success("Laid on the harbour master's desk.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (notFound) {
    return (
      <Container className="py-32 text-center">
        <h1 className="font-serif text-3xl text-[#1E1E2F] mb-3">No listing at these coordinates</h1>
        <p className="text-[#505A68] mb-6">This maker may have weighed anchor. Try the directory.</p>
        <GoldButton as="link" to="/made">Back to the Made directory</GoldButton>
      </Container>
    );
  }

  if (!listing) {
    return (
      <div className="py-40 flex justify-center">
        <Loader2 className="animate-spin text-[#CDA434]" size={32} />
      </div>
    );
  }

  return (
    <div>
      <div className="relative h-[340px] sm:h-[420px] overflow-hidden border-b border-[#D6D3CB]">
        <img src={listing.image_url || "https://images.pexels.com/photos/8755712/pexels-photo-8755712.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200"} alt={listing.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1E1E2F] via-[#1E1E2F]/30 to-transparent" />
        <Container className="absolute inset-x-0 bottom-0 pb-8">
          <Link to="/made" className="inline-flex items-center gap-1 text-[#FFFAF3]/80 hover:text-[#FFFAF3] text-sm mb-4 link-underline">
            <ArrowLeft size={14} /> The Made directory
          </Link>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <ShoreBadge shore={listing.shore} />
            <PlanBadge plan={listing.plan} />
            <span className="bg-[#FFFAF3]/90 text-[#1E1E2F] text-xs font-sans uppercase tracking-wide px-2.5 py-0.5">{listing.category}</span>
          </div>
          <h1 className="font-serif font-bold text-3xl sm:text-5xl text-[#FFFAF3]">{listing.name}</h1>
        </Container>
      </div>

      <section className="py-16 sm:py-20">
        <Container>
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-7">
              <p className="font-playfair italic text-xl sm:text-2xl text-[#1E1E2F] leading-relaxed border-l-2 border-[#CDA434] pl-6 mb-10">
                {listing.origin_paragraph}
              </p>
              <h2 className="font-serif text-xl text-[#003366] mb-4">About {listing.name}</h2>
              <p className="font-sans text-base text-[#1B2430] leading-relaxed whitespace-pre-line">{listing.description}</p>

              {listing.products && listing.products.length > 0 && (
                <div className="mt-10" data-testid="listing-products">
                  <div className="flex items-center gap-3 mb-5">
                    <h2 className="font-serif text-xl text-[#003366]">Products & services</h2>
                    <div className="flex-1 h-px bg-[#CDA434]/40" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {listing.products.map((p) => (
                      <div key={p.id} className="bg-[#F8F1E7] border border-[#D6D3CB] p-5">
                        <span className={`inline-flex items-center text-[10px] font-semibold uppercase px-1.5 py-0.5 border mb-2 ${p.type === "service" ? "text-[#003366] border-[#003366]/30" : "text-[#7a611c] border-[#CDA434]/50"}`}>{p.type}</span>
                        <h3 className="font-serif text-base text-[#1E1E2F]">{p.name}</h3>
                        {p.price && <p className="font-sans text-sm font-semibold text-[#7a611c] mt-0.5">{p.price}</p>}
                        <p className="font-sans text-sm text-[#505A68] leading-relaxed mt-2">{p.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-8 pt-6 border-t border-[#D6D3CB] space-y-2 font-sans text-sm text-[#505A68]">
                <p className="flex items-center gap-2"><MapPin size={15} className="text-[#CDA434]" /> {listing.town}</p>
                {listing.website && (
                  <p className="flex items-center gap-2">
                    <Globe size={15} className="text-[#CDA434]" />
                    <a href={listing.website} target="_blank" rel="noreferrer" className="text-[#003366] link-underline">{listing.website.replace(/^https?:\/\//, "")}</a>
                  </p>
                )}
              </div>
            </div>

            {/* Enquiry form */}
            <div className="lg:col-span-5">
              <div className="bg-[#FFFAF3] border border-[#D6D3CB] p-8 sticky top-24">
                <h3 className="font-serif text-xl text-[#1E1E2F] mb-1">Send an enquiry</h3>
                <p className="font-sans text-sm text-[#505A68] mb-6">Your message goes straight to this maker's inbox.</p>

                {sent ? (
                  <div data-testid="enquiry-success" className="bg-[#F8F1E7] border border-[#CDA434] p-6 text-center">
                    <p className="font-playfair italic text-lg text-[#7a611c] mb-2">Laid on the harbour master's desk.</p>
                    <p className="font-sans text-sm text-[#505A68]">The maker will be in touch. In this preview, enquiries are stored and shown in the maker's account.</p>
                    <button onClick={() => setSent(false)} className="mt-4 font-sans text-sm font-semibold text-[#003366] link-underline">Send another</button>
                  </div>
                ) : (
                  <form onSubmit={submit} className="space-y-4" data-testid="enquiry-form">
                    <div>
                      <label className="block font-sans text-xs uppercase tracking-wider font-semibold text-[#1B2430] mb-1.5">Your name</label>
                      <input required data-testid="enquiry-name" value={form.from_name} onChange={(e) => setForm({ ...form, from_name: e.target.value })}
                        className="w-full bg-[#FFFAF3] border border-[#D6D3CB] focus:border-[#003366] px-4 py-2.5 text-sm font-sans" />
                    </div>
                    <div>
                      <label className="block font-sans text-xs uppercase tracking-wider font-semibold text-[#1B2430] mb-1.5">Your email</label>
                      <input required type="email" data-testid="enquiry-email" value={form.from_email} onChange={(e) => setForm({ ...form, from_email: e.target.value })}
                        className="w-full bg-[#FFFAF3] border border-[#D6D3CB] focus:border-[#003366] px-4 py-2.5 text-sm font-sans" />
                    </div>
                    <div>
                      <label className="block font-sans text-xs uppercase tracking-wider font-semibold text-[#1B2430] mb-1.5">Message</label>
                      <textarea required rows={4} data-testid="enquiry-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                        className="w-full bg-[#FFFAF3] border border-[#D6D3CB] focus:border-[#003366] px-4 py-2.5 text-sm font-sans resize-none" />
                    </div>
                    <GoldButton type="submit" disabled={sending} data-testid="enquiry-submit" className="w-full">
                      {sending ? <Loader2 size={16} className="animate-spin" /> : "Send enquiry"}
                    </GoldButton>
                  </form>
                )}
              </div>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
